import { Children, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'redux-form';
import { media, spacing, size } from '../ui';
import { MdRadioButtonUnchecked } from 'react-icons/md';
import { MdRadioButtonChecked } from 'react-icons/md';
import Avatar from 'components/Avatar';
import { getAttachmentURL } from 'utils';

export const RadioCardDescription = styled.span`
  display: block;
  margin-top: ${spacing(0.5)};
`;

const LabelLabel = styled.span`
  flex: 1;
`;

const ChildrenElement = styled.div`
  color: ${({ theme }) => theme.textLight};
`;

const Radio = ({ name, label, value, checked, children, onChange }) => (
  <label>
    <input
      type="radio"
      name={name}
      value={value}
      onChange={e => onChange(e, e.target.value)}
    />
    {children}
  </label>
);

const RadioCardWrapper = styled.label`
  display: block;
  padding: ${spacing()};
  border-radius: ${({ theme }) => theme.borderRadius};
  margin-bottom: ${spacing()};
  cursor: pointer;
  box-shadow: inset 0 0 0 2px ${({ theme }) => theme.separator};

  ${props =>
    props.checked &&
    css`
      box-shadow: inset 0 0 0 2px ${({ theme }) => theme.primary};
    `};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `};

  ${props =>
    props.columns &&
    css`
      flex: 1;

      + * {
        ${media.tablet`margin-left: ${spacing(2)}`};
      }
    `};
`;

const RadioCardLabel = styled.p`
  font-size: ${size(1)};
  margin-top: 0;
  margin-bottom: ${spacing()};
  font-weight: 600;

  svg {
    margin-right: ${spacing()};

    ${props =>
      props.checked &&
      css`
        color: ${({ theme }) => theme.primary};
      `};
  }

  ${props =>
    props.variant === 'bare' &&
    css`
      margin-bottom: 0;
    `};

  ${props =>
    props.variant === 'withAvatar' &&
    css`
      display: flex;
      align-items: center;
      flex-direction: row-reverse;
      margin-bottom: 0;

      svg {
        margin-right: 0;
        margin-left: ${spacing()};
      }

      ${LabelLabel} {
        margin-left: ${spacing()};
      }
    `};
`;

const RadioButtonGroupWrapper = styled.div`
  ${props =>
    props.columns &&
    css`
      display: flex;
      justify-content: center;
      flex-direction: column;

      ${media.tablet`
        flex-direction: row;
        margin-right: 0;
        `};
    `};
`;

export const RadioCard = ({
  name,
  label,
  value,
  checked,
  children,
  onChange,
  alwaysExpanded,
  disabled,
  columns,
  renderHeader,
  hideButton,
  avatarUser,
}) => (
  <RadioCardWrapper checked={checked} disabled={disabled} columns={columns}>
    <input
      type="radio"
      name={name}
      value={value}
      disabled={disabled}
      onChange={e => onChange(e, e.target.value)}
      style={{ display: 'none' }}
    />

    {Boolean(renderHeader) && renderHeader()}

    <RadioCardLabel
      checked={checked}
      variant={Boolean(avatarUser) ? 'withAvatar' : !children ? 'bare' : ''}
    >
      {!hideButton &&
        (checked ? <MdRadioButtonChecked /> : <MdRadioButtonUnchecked />)}
      <LabelLabel>{label}</LabelLabel>
      {Boolean(avatarUser) && (
        <Avatar
          {...avatarUser}
          medium
          avatarURL={
            Boolean(avatarUser.avatarUrl) &&
            getAttachmentURL(avatarUser.avatarUrl)
          }
        />
      )}
    </RadioCardLabel>

    <ChildrenElement>{children}</ChildrenElement>
  </RadioCardWrapper>
);

export const RadioButtonGroup = ({
  children,
  name,
  value,
  onChange,
  columns,
  ...other
}) => (
  <RadioButtonGroupWrapper role="radiogroup" columns={columns} {...other}>
    {Children.map(children, (child, index) =>
      cloneElement(child, {
        name,
        key: index,
        // eslint-disable-next-line
        checked: value == child.props.value,
        onChange,
        columns,
      }),
    )}
  </RadioButtonGroupWrapper>
);

const renderRadioGroup = ({ input, ...rest }) => (
  <RadioButtonGroup
    {...input}
    {...rest}
    selected={input.value}
    onChange={(event, value) => input.onChange(value)}
  />
);

export const ConnectedRadioGroup = ({ name, ...rest }) => (
  <Field name={name} component={renderRadioGroup} {...rest} />
);

export default Radio;
